import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import type { Participant } from "@vc/models"

import { VideoTile } from "@vc/views/vcLayout/components/video/VideoTile"
import { ParticipantGroupTile } from "@vc/views/vcLayout/components/video/ParticipantGroupTile"
import { useResponsiveDevice } from "@vc/viewmodels/hooks/useResponsiveDevice"
import { AUTO_PAGE_SIZE, visibleTilesPerPage } from "@/features/meeting/store/meeting-layout.store"
import { User } from "lucide-react"
import { computeGridRects } from "./tile-geometry"
import { TileLayer, type TileSlot } from "./TileLayer"
import "./VideoGrid.css"

/**
 * Tiles per page when the caller does not say.
 *
 * The live grids always say — MeetingScreen passes `pageSize` from the Settings
 * density — so this is the floor under the call sites that render a fixed, tiny
 * set (a single spotlight tile) and have no density to consult. It tracks the
 * Auto page size rather than a number of its own, so the fallback can never
 * quietly disagree with the setting.
 */
export const DEFAULT_PAGE_SIZE = AUTO_PAGE_SIZE

/** Slot identity of the "+N others" tile — never a participant id. */
const GROUP_TILE_KEY = "__group-tile__"

type ContainerSize = {
  width: number
  height: number
  /** Why the size last changed: an orientation change settles once and may
   *  animate; every other resize must be tracked instantly. */
  source: "resize" | "orientation"
}

function useContainerSize(ref: React.RefObject<HTMLDivElement | null>): ContainerSize {
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0, source: "resize" })
  const prevSizeRef = useRef({ width: 0, height: 0 })
  const frozenRef = useRef(false)

  // Layout effect, so the first measurement lands before the first paint and
  // the grid never shows a frame with no tiles in it.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    let rafId: number | null = null
    const timers = new Set<ReturnType<typeof setTimeout>>()
    const later = (fn: () => void, ms: number) => {
      const t = setTimeout(() => {
        timers.delete(t)
        fn()
      }, ms)
      timers.add(t)
    }
    let detachViewport: (() => void) | null = null

    // ── Core size updater ──────────────────────────────────────────────
    const commitSize = (width: number, height: number, source: ContainerSize["source"]) => {
      if (width <= 0 || height <= 0) return
      const prev = prevSizeRef.current
      if (Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) return
      prevSizeRef.current = { width, height }
      setSize({ width, height, source })
    }

    // RAF-throttled, and blocked while an orientation change is settling
    const throttledApply = (width: number, height: number) => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = null
        if (!frozenRef.current) commitSize(width, height, "resize")
      })
    }

    // ── ResizeObserver — primary source ────────────────────────────────
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      throttledApply(width, height)
    })
    ro.observe(el)

    // ── Window resize — browser zoom fallback ─────────────────────────
    const handleResize = () => {
      const rect = el.getBoundingClientRect()
      throttledApply(rect.width, rect.height)
    }
    window.addEventListener("resize", handleResize)

    // ── Orientation change ────────────────────────────────────────────
    // FREEZE the layout immediately, wait for the viewport to stabilise, then
    // commit the final dimensions once. Android Chrome's intermediate
    // half-screen states never reach the grid.
    const handleOrientationChange = () => {
      frozenRef.current = true
      detachViewport?.()
      detachViewport = null

      let settled = false
      const settle = () => {
        if (settled) return
        settled = true
        detachViewport?.()
        detachViewport = null
        // One final read after the viewport is stable
        later(() => {
          const rect = el.getBoundingClientRect()
          frozenRef.current = false
          commitSize(rect.width, rect.height, "orientation")
        }, 50)
      }

      const vv = window.visualViewport
      if (vv) {
        // visualViewport fires a final "resize" once the browser has finished
        // the orientation animation (most reliable on Android); 150ms of
        // silence after the last one means stable.
        let vvTimer: ReturnType<typeof setTimeout> | null = null
        const onVVResize = () => {
          if (vvTimer) clearTimeout(vvTimer)
          vvTimer = setTimeout(settle, 150)
        }
        vv.addEventListener("resize", onVVResize)
        detachViewport = () => {
          vv.removeEventListener("resize", onVVResize)
          if (vvTimer) clearTimeout(vvTimer)
        }
        // Safety: if visualViewport never fires (e.g. desktop), settle anyway
        later(settle, 500)
      } else {
        later(settle, 400)
      }
    }

    window.addEventListener("orientationchange", handleOrientationChange)
    screen.orientation?.addEventListener?.("change", handleOrientationChange)

    // ── Initial size ──────────────────────────────────────────────────
    const rect = el.getBoundingClientRect()
    commitSize(rect.width, rect.height, "resize")

    return () => {
      ro.disconnect()
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("orientationchange", handleOrientationChange)
      screen.orientation?.removeEventListener?.("change", handleOrientationChange)
      if (rafId) cancelAnimationFrame(rafId)
      timers.forEach((t) => clearTimeout(t))
      detachViewport?.()
      frozenRef.current = false
    }
  }, [ref])

  return size
}

/**
 * The participant grid.
 *
 * Pages the roster, caps it on touch devices, and draws each tile at the
 * rectangle `computeGridRects` gives it — as siblings in one TileLayer, so a
 * change in how the rows are split moves tiles rather than remounting them.
 */
export const VideoGrid = memo(function VideoGrid({
  participants,
  pageSize,
  variant = "auto",
  reactionFor,
  onPin,
  onPinForAll,
  pinnedForAllId,
  onMaximize,
  onOpenChat,
  onOpenParticipants,
  virtualBackgroundUrl,
  tileKey,
  showEmptyState = true,
}: {
  participants: Participant[]
  pageSize?: number
  variant?: "auto" | "legacy"
  /** Emoji for one participant, or null. A grid renders many tiles, so it
   *  needs the lookup rather than a single value — passing one emoji here is
   *  what put the same reaction in the corner of every tile. */
  reactionFor?: (peerId: string | undefined | null) => string | null
  onPin?: (participantId: string) => void
  /** Moderator only: pin a tile on everyone's screen. */
  onPinForAll?: (participantId: string) => void
  pinnedForAllId?: string | null
  onMaximize?: (element: HTMLElement) => void
  onOpenChat?: (participantId: string) => void
  onOpenParticipants?: () => void
  virtualBackgroundUrl?: string | null
  /**
   * Slot identity for a tile. Defaults to the participant id, which is right
   * for a grid of people. A stage that shows whoever is speaking passes a
   * constant instead, so a change of speaker re-targets the one stage tile
   * rather than tearing it down and animating a new one in.
   */
  tileKey?: (participant: Participant) => string
  /**
   * Whether an empty grid says "No participants". Off when the caller knows
   * better — "hide tiles without video" empties the grid while people are
   * still in the room, and MeetingScreen draws its own notice for that.
   */
  showEmptyState?: boolean
}) {
  // Memoize callbacks to prevent VideoTile re-renders
  const stableOnPin = useCallback((id: string) => onPin?.(id), [onPin])
  const stableOnMaximize = useCallback((el: HTMLElement) => onMaximize?.(el), [onMaximize])
  const stableOnOpenChat = useCallback((id: string) => onOpenChat?.(id), [onOpenChat])
  const stableOnOpenParticipants = useCallback(() => onOpenParticipants?.(), [onOpenParticipants])
  const containerRef = useRef<HTMLDivElement>(null)
  const { width, height, source: resizeSource } = useContainerSize(containerRef)
  
  // Get actual device type - not just screen width
  const { isMobileDevice: isActualMobileDevice, isTabletDevice } = useResponsiveDevice()
  // Phones and tablets show at most six tiles a page. Past that they used to
  // fold everyone else into an "N others" tile that opened the participant
  // list — names, no video, and on a phone often nothing visible at all — so
  // the seventh person onwards could never be watched. They page instead:
  // swipe, the arrows, or the dots below.
  const isTouchLimitedDevice = isActualMobileDevice || isTabletDevice

  const totalCount = participants.length
  const requestedPageSize = pageSize && pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE
  // Shared with MeetingScreen's active-speaker seating, so the page drawn here
  // and the page the speaker is seated onto are the same page.
  const effectivePageSize = visibleTilesPerPage(requestedPageSize, isTouchLimitedDevice)
  const pageCount = Math.max(1, Math.ceil(totalCount / effectivePageSize))
  const [pageIndex, setPageIndex] = useState(0)
  const [showPaginationTip, setShowPaginationTip] = useState(false)
  const effectivePageIndex = Math.min(pageIndex, pageCount - 1)

  /**
   * Changing the density puts you back on the first page.
   *
   * Clamping alone is not enough. Going from 2 tiles a page to 20 leaves page 7
   * in range only if the room is large, so the same click lands you somewhere
   * arbitrary in a big room and on page 1 in a small one. Worse, the first page
   * is where your own tile and the promoted active speaker live, and a density
   * change is the user asking to see the grid differently — showing them the
   * middle of it is not an answer to that.
   */
  useEffect(() => {
    setPageIndex(0)
  }, [effectivePageSize])
  
  // Show pagination tip when arrows first appear (more than 16 participants)
  useEffect(() => {
    if (pageCount > 1) {
      // Bug fix: previously called removeItem then getItem on the same key (always null → always showed tip)
      const hasSeenTip = localStorage.getItem('inmeet_pagination_tip_seen')
      if (!hasSeenTip) {
        setShowPaginationTip(true)
        const timer = setTimeout(() => {
          setShowPaginationTip(false)
        }, 5000)
        return () => clearTimeout(timer)
      }
    }
  }, [pageCount])
  const hidePaginationTip = () => {
    setShowPaginationTip(false)
    localStorage.setItem('inmeet_pagination_tip_seen', 'true')
  }

  const basePageParticipants = pageCount > 1
    ? participants.slice(
        effectivePageIndex * effectivePageSize,
        effectivePageIndex * effectivePageSize + effectivePageSize,
      )
    : participants
  
  // The page already fits (see TOUCH_DEVICE_MAX_TILES), so there is no
  // overflow tile any more.
  const displayParticipants = basePageParticipants
  const remainingParticipants: Participant[] = []
  const showGroupTile = false

  // Swipe between pages on a touch screen.
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null)
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    swipeStartRef.current = t ? { x: t.clientX, y: t.clientY } : null
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = swipeStartRef.current
    swipeStartRef.current = null
    const t = e.changedTouches[0]
    if (!start || !t || pageCount <= 1) return
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return
    setPageIndex(dx < 0 ? Math.min(pageCount - 1, effectivePageIndex + 1) : Math.max(0, effectivePageIndex - 1))
    hidePaginationTip()
  }
  
  const pageParticipants = displayParticipants
  const count = pageParticipants.length + (showGroupTile ? 1 : 0) // Include group tile in count

  const rects = useMemo(
    () => computeGridRects(count, width, height, variant),
    [count, width, height, variant],
  )

  const slots: TileSlot[] = []
  if (rects.length === count) {
    pageParticipants.forEach((p, i) => {
      slots.push({
        key: tileKey ? tileKey(p) : p.id,
        rect: rects[i],
        content: (
          <VideoTile
            participant={p}
            reaction={reactionFor?.(p.id) ?? null}
            isPinned={false}
            onPin={stableOnPin}
            onPinForAll={onPinForAll}
            pinnedForAllId={pinnedForAllId}
            onMaximize={stableOnMaximize}
            onOpenChat={stableOnOpenChat}
            virtualBackgroundUrl={p.isLocal ? virtualBackgroundUrl ?? null : null}
          />
        ),
      })
    })
    if (showGroupTile) {
      slots.push({
        key: GROUP_TILE_KEY,
        rect: rects[count - 1],
        content: (
          <ParticipantGroupTile
            participants={remainingParticipants}
            onOpenParticipants={stableOnOpenParticipants}
          />
        ),
      })
    }
  }

  // One container and one TileLayer whether or not anyone is here. The size
  // observer is attached to the container once, so swapping in a different
  // element for the empty state left it measuring a detached node; and the
  // layer staying mounted is what lets the first person into an empty grid
  // arrive with an entrance rather than simply appear.
  return (
    <div
      ref={containerRef}
      className="video-grid-container relative h-full w-full overflow-hidden"
      onTouchStart={isTouchLimitedDevice ? onTouchStart : undefined}
      onTouchEnd={isTouchLimitedDevice ? onTouchEnd : undefined}
    >
      {totalCount === 0 && showEmptyState && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm text-white/40">No participants</span>
        </div>
      )}
      {pageCount > 1 && (
        <>
          <button
            type="button"
            onClick={() => {
              setPageIndex(Math.max(0, effectivePageIndex - 1))
              hidePaginationTip()
            }}
            disabled={effectivePageIndex === 0}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              height: 40,
              width: 40,
              borderRadius: 9999,
              background: "rgba(0,0,0,0.45)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.92)",
              opacity: effectivePageIndex === 0 ? 0.35 : 1,
            }}
            aria-label="Previous page"
          >
            ‹
          </button>
          <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", zIndex: 20 }}>
            {showPaginationTip && (
              <div
                className="video-grid-pagination-tip"
                style={{
                  position: "absolute",
                  right: 50,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "#202124",
                  color: "white",
                  padding: "10px 14px",
                  borderRadius: 10,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)",
                  pointerEvents: "none",
                  border: "1px solid rgba(244, 240, 240, 0.15)",
                }}
              >
                <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 16 }}><User className="w-4 h-4 text-primary"/></span>
                  More participants
                </div>
                <div style={{ fontSize: 11, opacity: 0.85, marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                  Click
                  <span className="video-grid-pagination-arrow" style={{ 
                    display: "inline-flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    width: 16, 
                    height: 16, 
                    borderRadius: "50%", 
                    background: "rgba(255,255,255,0.2)",
                    fontSize: 12,
                  }}>›</span>
                  to view
                </div>
                <div
                  style={{
                    position: "absolute",
                    right: -8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 0,
                    height: 0,
                    borderTop: "8px solid transparent",
                    borderBottom: "8px solid transparent",
                    borderLeft: "8px solid rgba(0,0,0,0.9)",
                  }}
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setPageIndex(Math.min(pageCount - 1, effectivePageIndex + 1))
                hidePaginationTip()
              }}
              disabled={effectivePageIndex >= pageCount - 1}
              style={{
                height: 40,
                width: 40,
                borderRadius: 9999,
                background: "rgba(0,0,0,0.45)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.92)",
                opacity: effectivePageIndex >= pageCount - 1 ? 0.35 : 1,
                cursor: effectivePageIndex >= pageCount - 1 ? "not-allowed" : "pointer",
              }}
              aria-label="Next page"
            >
              ›
            </button>
          </div>
          {/* Top CENTRE, not top-right.

              The grid has no padding — TileLayer fills inset-0 — so the corner
              tile reaches the container's corner, and a tile puts its own
              status pills at `right-2 top-2`. This counter sat at top/right 10
              on the same z-index, so `2/2` and the top-right participant's
              muted-microphone badge were drawn on top of each other and neither
              could be read. The rails escape this by reserving `padTop: 48` for
              their arrows; the grid reserves nothing.

              Centre is the one edge position no tile overlay claims: pills go
              right, the raised-hand badge left, the name bar along the bottom,
              pin and maximise bottom-right. */}
          <div
            style={{
              position: "absolute",
              top: 10,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 20,
              fontSize: 12,
              color: "rgba(255,255,255,0.6)",
              background: "rgba(0,0,0,0.35)",
              border: "1px solid rgba(255,255,255,0.10)",
              padding: "4px 8px",
              borderRadius: 9999,
              userSelect: "none",
            }}
          >
            {effectivePageIndex + 1}/{pageCount}
          </div>
        </>
      )}
      <TileLayer
        slots={slots}
        width={width}
        height={height}
        animateResize={resizeSource === "orientation"}
        className="tile-layer--cover"
      />
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  // Only re-render if participants array content actually changed
  if (prevProps.participants.length !== nextProps.participants.length) return false
  // The density, and therefore how many of them are on this page. Left out of
  // this list the grid would keep drawing the old page size until something
  // else happened to change — so the Settings dropdown would appear to do
  // nothing at all until the next person joined.
  if (prevProps.pageSize !== nextProps.pageSize) return false
  if (prevProps.variant !== nextProps.variant) return false
  if (prevProps.reactionFor !== nextProps.reactionFor) return false
  if (prevProps.virtualBackgroundUrl !== nextProps.virtualBackgroundUrl) return false
  if (prevProps.tileKey !== nextProps.tileKey) return false
  
  // Check if participant data actually changed
  for (let i = 0; i < prevProps.participants.length; i++) {
    const prev = prevProps.participants[i]
    const next = nextProps.participants[i]
    if (!prev || !next) return false
    if (
      prev.id !== next.id ||
      prev.name !== next.name ||
      // Stream identity changes when the peer's track set changes (new/replaced
      // tracks) — must re-render so the tile re-binds, else it shows a blank frame.
      prev.stream !== next.stream ||
      prev.muted !== next.muted ||
      prev.videoOn !== next.videoOn ||
      // The camera-off icon reads the person's own camera, which can change
      // while `videoOn` stays false (this viewer paused incoming videos).
      prev.cameraOn !== next.cameraOn ||
      prev.handRaised !== next.handRaised ||
      prev.isLocal !== next.isLocal ||
      prev.isSpeaking !== next.isSpeaking ||
      prev.isScreenSharing !== next.isScreenSharing ||
      prev.screenShareStopping !== next.screenShareStopping ||
      prev.role !== next.role ||
      prev.connectionQuality !== next.connectionQuality ||
      // Additional-camera tiles differ from their owner only by these, so
      // without them a source swap would keep the previous device's label.
      prev.sourceKind !== next.sourceKind ||
      prev.sourceLabel !== next.sourceLabel
    ) {
      return false
    }
  }
  
  return true
})
